/* @flow */
import CurrentUser from 'auth/CurrentUser';
import Favicon from 'lib/Favicon';
import Store from 'lib/Store';
import Unread from 'entities/Unread';
import UnreadActions from 'actions/UnreadActions';
import Unreads from 'collections/Unreads';
import ChatActions from 'chat/actions/ChatActions';
import { ActionTypes as ChatActionTypes } from 'chat/constants/ChatConstants';
import { ActionTypes } from 'constants/UnreadConstants';
import { PushEvents } from 'pusher/PusherConstants';
import { chatMessageFactory, shouldListenToPushEventFrom } from 'entities/ChatMessage';

const THREAD_ID = 'threads';

const threadKey = (unreadsKey: string) => `${THREAD_ID}-${unreadsKey}`;

// Note: Flux stores are now a DEPRECATED pattern. See docs/GraphQL.md
class UnreadStore extends Store {
  getInitialState() {
    return {
      unreads: new Unreads(),
    };
  }

  getUnreads(): Unreads {
    return this.state.unreads || new Unreads();
  }

  getTotalUnread(): number {
    return this.getUnreadCount('user');
  }

  getThreadUnreadCount(threadUuid: string): number {
    return this.getUnreadCount(threadKey(threadUuid));
  }

  getUnreadCount(unreadsKey: string): number {
    const unread = this.state.unreads.get(unreadsKey);
    if (unread) {
      // If a parent unread has been set as 0, then all the children count as 0 implicitly
      let iter = unread;
      while (iter.hasParent()) {
        iter = this.state.unreads.get(iter.getParentId());
        if (!iter || iter.getUnreadCount() === 0) {
          return 0;
        }
      }
      return unread.getUnreadCount();
    } else {
      return 0;
    }
  }

  incrementUnreads(unreadsKey: string, parent: any): void {
    let unread = this.state.unreads.get(unreadsKey);
    if (!unread) {
      this.addNewUnread(new Unread({ unreadsKey, parent }));
      unread = this.state.unreads.get(unreadsKey);
    }
    let iter = unread;
    while (iter) {
      iter.incrementUnreadCount();
      iter = this.state.unreads.get(iter.getParentId());
    }
  }

  decrementUnreads(unreadsKey: string, count: number): void {
    let iter = this.state.unreads.get(unreadsKey);
    while (iter) {
      iter.decrementUnreadCountBy(count);
      iter = this.state.unreads.get(iter.getParentId());
    }
  }

  setThreadAsRead(threadUuid: string): void {
    this.setAsRead(threadKey(threadUuid));
  }

  setAsRead(unreadsKey: string): void {
    const unread = this.state.unreads.get(unreadsKey);
    if (unread) {
      const numUnread = unread.getUnreadCount();
      let iter = unread;
      while (iter) {
        iter.decrementUnreadCountBy(numUnread);
        iter = this.state.unreads.get(iter.getParentId());
      }
    }
  }

  addNewThreadUnread(unread: Unread): void {
    this.state.unreads.add({
      id: threadKey(unread.id.toString()),
      parent: THREAD_ID,
      unread_count: unread.getUnreadCount(),
    });
  }

  addNewUnread(unread: Unread): void {
    if (unread.getParentId() === THREAD_ID) {
      this.addNewThreadUnread(unread);
      return;
    }
    this.state.unreads.add(unread);
  }

  messageIsForCurrentChatThread(chatId: string): boolean {
    return window.location.pathname.replace(/\/+$/, '').endsWith(chatId);
  }

  refreshFavicon(): void {
    Favicon.showNumber(this.getTotalUnread());
  }

  handleDispatch(action: Object): void {
    switch (action.type) {
      case ChatActionTypes.LIST_CHATS:
        action.chats.forEach(chat =>
          this.addNewUnread(
            new Unread({
              id: chat.id,
              parent: 'chats',
              unread_count: chat.getUnreadMessages(),
            })
          )
        );
        this.refreshFavicon();
        this.emitChange();
        break;
      case ChatActionTypes.MARK_READ:
      case ChatActionTypes.READ_MESSAGE:
        this.setAsRead(action.chatId);
        this.refreshFavicon();
        this.emitChange();
        break;
      case ActionTypes.CLEAR_THREAD_UNREADS:
        this.setThreadAsRead(action.threadUuid);
        this.refreshFavicon();
        this.emitChange();
        break;
      case ActionTypes.LIST_UNREADS:
        // action.unreads contains a complete description of all unreads, so we reset the state
        this.state = this.getInitialState();

        action.unreads.forEach(unread => this.addNewUnread(new Unread(unread)));
        this.refreshFavicon();
        this.emitChange();
        break;
      case PushEvents.CHANGESET_CREATE_CHAT_MESSAGE: {
        const messages = action.changes.map(change => chatMessageFactory(change.content));

        // Structures to avoid useless update & duplicate
        // (eg only mark "read up to" for the last message per chat).
        const chatToMarkReadUpTo = new Map();
        const chatToIncrementUnread = new Set();

        /* For each received message:
         * If the message is in the current thread, mark it a read.
         * Otherwise load & increment the unread for the chat.
         */
        messages.forEach(message => {
          const chatId = message.getChatId();

          if (
            !shouldListenToPushEventFrom(message) ||
            message.getSender().getUuid() === CurrentUser.getUserUuid()
          ) {
            return;
          }

          if (this.messageIsForCurrentChatThread(chatId)) {
            const seq = chatToMarkReadUpTo.get(chatId);
            if (!seq || message.getSeq() > seq) {
              chatToMarkReadUpTo.set(chatId, message.getSeq());
            }
          } else {
            chatToIncrementUnread.add(chatId);
          }
        });

        chatToMarkReadUpTo.forEach((seqNumber, chatId) => {
          setTimeout(() => ChatActions.markReadUpto(chatId, seqNumber), 0);
        });

        chatToIncrementUnread.forEach(chatId => {
          this.incrementUnreads(chatId, 'chats');
        });

        if (chatToIncrementUnread.size > 0) {
          UnreadActions.loadUnreads();
          this.refreshFavicon();
          this.emitChange();
        }

        break;
      }
      case PushEvents.CHANGESET_UPDATE_GROUP:
      case PushEvents.CHANGESET_UPDATE_UNREADS:
        UnreadActions.loadUnreads();
        break;
      default:
        break;
    }
  }
}

export default new UnreadStore();
